/**
 * PROFILE PHOTO SHEET (Phase 6)
 * =============================
 *
 * The member chooses a picture of themselves, frames it, and keeps it — or takes it away.
 *
 * Device-native picker and device-native camera. No custom camera UI, no filters, no editing
 * tools, no rotation suite. One square frame: drag to position, pinch (or the zoom control) to
 * size, Cancel or Save.
 *
 * The square the member framed is what gets uploaded, so what they saw is what is stored. The
 * server normalises and bounds it again on arrival — this is presentation, not the guarantee.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, ImagePlus, Trash2, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OUTPUT_EDGE = 1024;
const MAX_INPUT_BYTES = 8 * 1024 * 1024;
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;

const COPY = {
  en: {
    title: 'Your picture',
    subtitle: 'A photo of you, shown where your name is shown.',
    choose: 'Choose Photo',
    take: 'Take Photo',
    remove: 'Remove Photo',
    cancel: 'Cancel',
    save: 'Save',
    frameHint: 'Drag to position. Pinch or use the slider to zoom.',
    saving: 'Saving…',
    removing: 'Removing…',
    tooLarge: 'That photo is larger than 8 MB. Please choose a smaller one.',
    unreadable: 'That file could not be read as a photo. Please choose another.',
    failed: 'That did not save. Please try again.',
    removeFailed: 'That did not remove. Please try again.',
  },
  es: {
    title: 'Tu foto',
    subtitle: 'Una foto tuya, donde aparece tu nombre.',
    choose: 'Elegir foto',
    take: 'Tomar foto',
    remove: 'Quitar foto',
    cancel: 'Cancelar',
    save: 'Guardar',
    frameHint: 'Arrastra para colocar. Pellizca o usa el control para acercar.',
    saving: 'Guardando…',
    removing: 'Quitando…',
    tooLarge: 'Esa foto pesa más de 8 MB. Por favor elige una más pequeña.',
    unreadable: 'No se pudo leer ese archivo como foto. Por favor elige otro.',
    failed: 'No se guardó. Por favor inténtalo de nuevo.',
    removeFailed: 'No se quitó. Por favor inténtalo de nuevo.',
  },
};

export const ProfilePhotoSheet = ({ hasPhoto, onSaved, onRemoved, onClose, lang = 'en' }) => {
  const t = COPY[lang] || COPY.en;

  const chooseRef = useRef(null);
  const cameraRef = useRef(null);
  const imageRef = useRef(null);
  const pointers = useRef(new Map());
  const pinch = useRef(null);

  const [source, setSource] = useState(null);
  const [natural, setNatural] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const frame = useMemo(
    () => Math.max(220, Math.min(320, (typeof window !== 'undefined' ? window.innerWidth : 360) - 72)),
    []
  );

  useEffect(() => () => { if (source) URL.revokeObjectURL(source); }, [source]);

  const geometry = useCallback(
    (z) => {
      if (!natural) return { scale: 1, width: frame, height: frame };
      const scale = (frame / Math.min(natural.width, natural.height)) * z;
      return { scale, width: natural.width * scale, height: natural.height * scale };
    },
    [natural, frame]
  );

  const clamp = useCallback(
    (next, z) => {
      const { width, height } = geometry(z);
      const maxX = Math.max(0, (width - frame) / 2);
      const maxY = Math.max(0, (height - frame) / 2);
      return {
        x: Math.min(maxX, Math.max(-maxX, next.x)),
        y: Math.min(maxY, Math.max(-maxY, next.y)),
      };
    },
    [geometry, frame]
  );

  const applyZoom = (z) => {
    const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
    setZoom(next);
    setOffset((prev) => clamp(prev, next));
  };

  const pickFile = (file) => {
    setError('');
    if (!file) return;
    if (file.size > MAX_INPUT_BYTES) {
      setError(t.tooLarge);
      return;
    }
    const url = URL.createObjectURL(file);
    const probe = new Image();
    probe.onload = () => {
      setNatural({ width: probe.naturalWidth, height: probe.naturalHeight });
      setSource(url);
      setZoom(1);
      setOffset({ x: 0, y: 0 });
    };
    probe.onerror = () => {
      URL.revokeObjectURL(url);
      setError(t.unreadable);
    };
    probe.src = url;
  };

  // ── framing gestures ──────────────────────────────────────────────────────────────────
  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinch.current = { distance: Math.hypot(a.x - b.x, a.y - b.y), zoom };
    }
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    const previous = pointers.current.get(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size >= 2 && pinch.current) {
      const [a, b] = [...pointers.current.values()];
      const distance = Math.hypot(a.x - b.x, a.y - b.y);
      if (pinch.current.distance > 0) {
        applyZoom(pinch.current.zoom * (distance / pinch.current.distance));
      }
      return;
    }

    const dx = e.clientX - previous.x;
    const dy = e.clientY - previous.y;
    setOffset((prev) => clamp({ x: prev.x + dx, y: prev.y + dy }, zoom));
  };

  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
  };

  // ── saving ────────────────────────────────────────────────────────────────────────────
  const cropped = () =>
    new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      canvas.width = OUTPUT_EDGE;
      canvas.height = OUTPUT_EDGE;
      const ctx = canvas.getContext('2d');
      const factor = OUTPUT_EDGE / frame;
      const { width, height } = geometry(zoom);
      const drawWidth = width * factor;
      const drawHeight = height * factor;
      ctx.drawImage(
        imageRef.current,
        (OUTPUT_EDGE - drawWidth) / 2 + offset.x * factor,
        (OUTPUT_EDGE - drawHeight) / 2 + offset.y * factor,
        drawWidth,
        drawHeight
      );
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.92);
    });

  const save = async () => {
    setBusy('save');
    setError('');
    try {
      const blob = await cropped();
      const form = new FormData();
      form.append('file', blob, 'profile-photo.jpg');
      const res = await axios.post(`${API}/profile/photo`, form);
      if (onSaved) await onSaved(res.data);
      if (onClose) onClose();
    } catch (err) {
      setError(t.failed);
    } finally {
      setBusy('');
    }
  };

  const remove = async () => {
    setBusy('remove');
    setError('');
    try {
      await axios.delete(`${API}/profile/photo`);
      if (onRemoved) await onRemoved();
      if (onClose) onClose();
    } catch (err) {
      setError(t.removeFailed);
    } finally {
      setBusy('');
    }
  };

  const { width, height } = geometry(zoom);

  // Rendered into the document body on purpose. Profile mounts this from inside animated
  // (transformed) sections, which are their own stacking contexts — a sheet left in place there
  // renders under the bottom navigation and the navigation eats the taps meant for Save.
  return createPortal(
    <AnimatePresence>
      <motion.div
        className="photo-sheet__scrim"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={() => !busy && onClose && onClose()}
        data-testid="profile-photo-scrim"
      >
        <motion.div
          className="photo-sheet"
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
          onClick={(e) => e.stopPropagation()}
          data-testid="profile-photo-sheet"
        >
          <header className="photo-sheet__head">
            <div>
              <p className="photo-sheet__title">{t.title}</p>
              <p className="photo-sheet__sub">{source ? t.frameHint : t.subtitle}</p>
            </div>
            <button
              className="photo-sheet__close"
              onClick={() => !busy && onClose && onClose()}
              data-testid="profile-photo-close"
            >
              <X size={18} />
            </button>
          </header>

          {error && (
            <p className="photo-sheet__error" data-testid="profile-photo-error">{error}</p>
          )}

          {!source ? (
            <div className="photo-sheet__actions">
              <button
                className="photo-sheet__action"
                onClick={() => chooseRef.current?.click()}
                data-testid="profile-photo-choose"
              >
                <ImagePlus size={18} />
                <span>{t.choose}</span>
              </button>
              <button
                className="photo-sheet__action"
                onClick={() => cameraRef.current?.click()}
                data-testid="profile-photo-take"
              >
                <Camera size={18} />
                <span>{t.take}</span>
              </button>
              {hasPhoto && (
                <button
                  className="photo-sheet__action photo-sheet__action--destructive"
                  onClick={remove}
                  disabled={!!busy}
                  data-testid="profile-photo-remove"
                >
                  <Trash2 size={18} />
                  <span>{busy === 'remove' ? t.removing : t.remove}</span>
                </button>
              )}
            </div>
          ) : (
            <div className="photo-sheet__crop">
              <div
                className="photo-sheet__frame"
                style={{ width: frame, height: frame }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                data-testid="profile-photo-frame"
              >
                <img
                  ref={imageRef}
                  src={source}
                  alt=""
                  draggable={false}
                  style={{
                    width,
                    height,
                    transform: `translate(${offset.x}px, ${offset.y}px)`,
                    pointerEvents: 'none',
                  }}
                />
                <div className="photo-sheet__mask" />
              </div>
              <input
                type="range"
                min={MIN_ZOOM}
                max={MAX_ZOOM}
                step={0.01}
                value={zoom}
                onChange={(e) => applyZoom(parseFloat(e.target.value))}
                className="photo-sheet__zoom"
                aria-label="Zoom"
                data-testid="profile-photo-zoom"
              />
              <div className="photo-sheet__confirm">
                <button
                  className="photo-sheet__btn photo-sheet__btn--ghost"
                  onClick={() => { setSource(null); setNatural(null); }}
                  disabled={!!busy}
                  data-testid="profile-photo-crop-cancel"
                >
                  {t.cancel}
                </button>
                <button
                  className="photo-sheet__btn photo-sheet__btn--gold"
                  onClick={save}
                  disabled={!!busy}
                  data-testid="profile-photo-crop-save"
                >
                  {busy === 'save' ? t.saving : t.save}
                </button>
              </div>
            </div>
          )}

          <input
            ref={chooseRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/*"
            style={{ display: 'none' }}
            onChange={(e) => { pickFile(e.target.files?.[0]); e.target.value = ''; }}
            data-testid="profile-photo-input-library"
          />
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="user"
            style={{ display: 'none' }}
            onChange={(e) => { pickFile(e.target.files?.[0]); e.target.value = ''; }}
            data-testid="profile-photo-input-camera"
          />

          <style>{`
            .photo-sheet__scrim {
              position: fixed;
              inset: 0;
              z-index: 3000;
              background: rgba(6, 6, 10, 0.72);
              backdrop-filter: blur(10px);
              -webkit-backdrop-filter: blur(10px);
              display: flex;
              align-items: flex-end;
              justify-content: center;
            }
            .photo-sheet {
              width: 100%;
              max-width: 440px;
              background: #0C0C12;
              border: 1px solid rgba(212, 175, 55, 0.18);
              border-bottom: none;
              border-radius: 24px 24px 0 0;
              padding: 20px 20px calc(env(safe-area-inset-bottom, 0px) + 24px);
              box-shadow: 0 -20px 60px rgba(0, 0, 0, 0.6);
            }
            .photo-sheet__head {
              display: flex;
              align-items: flex-start;
              justify-content: space-between;
              gap: 16px;
              margin-bottom: 18px;
            }
            .photo-sheet__title {
              font-family: "Cormorant Garamond", Georgia, serif;
              font-size: 22px;
              color: #F4EFE7;
              margin-bottom: 4px;
            }
            .photo-sheet__sub {
              font-size: 13px;
              line-height: 1.45;
              color: rgba(244, 239, 231, 0.5);
            }
            .photo-sheet__close {
              flex-shrink: 0;
              width: 34px;
              height: 34px;
              border-radius: 50%;
              background: rgba(255, 255, 255, 0.05);
              border: 1px solid rgba(255, 255, 255, 0.1);
              color: rgba(244, 239, 231, 0.7);
              display: flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
            }
            .photo-sheet__error {
              margin-bottom: 14px;
              padding: 10px 12px;
              border-radius: 12px;
              background: rgba(244, 67, 54, 0.12);
              border: 1px solid rgba(244, 67, 54, 0.28);
              color: #EF9A9A;
              font-size: 13px;
            }
            .photo-sheet__actions { display: flex; flex-direction: column; gap: 10px; }
            .photo-sheet__action {
              display: flex;
              align-items: center;
              gap: 12px;
              width: 100%;
              padding: 15px 16px;
              border-radius: 14px;
              background: rgba(255, 255, 255, 0.04);
              border: 1px solid rgba(255, 255, 255, 0.08);
              color: #F4EFE7;
              font-size: 15px;
              cursor: pointer;
              transition: background-color 0.2s ease, border-color 0.2s ease;
            }
            .photo-sheet__action:hover { background: rgba(212, 175, 55, 0.08); border-color: rgba(212, 175, 55, 0.24); }
            .photo-sheet__action--destructive { color: #EF9A9A; }
            .photo-sheet__action--destructive:hover { background: rgba(244, 67, 54, 0.1); border-color: rgba(244, 67, 54, 0.26); }
            .photo-sheet__crop { display: flex; flex-direction: column; align-items: center; gap: 16px; }
            .photo-sheet__frame {
              position: relative;
              overflow: hidden;
              border-radius: 50%;
              background: #000;
              touch-action: none;
              cursor: grab;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            .photo-sheet__frame img { user-select: none; max-width: none; }
            .photo-sheet__mask {
              position: absolute;
              inset: 0;
              border-radius: 50%;
              border: 1px solid rgba(212, 175, 55, 0.45);
              box-shadow: inset 0 0 40px rgba(0, 0, 0, 0.35);
              pointer-events: none;
            }
            .photo-sheet__zoom { width: 100%; max-width: 320px; accent-color: #D4AF37; }
            .photo-sheet__confirm { display: flex; gap: 12px; width: 100%; }
            .photo-sheet__btn {
              flex: 1;
              padding: 13px 18px;
              border-radius: 14px;
              font-size: 14px;
              font-weight: 600;
              cursor: pointer;
              transition: opacity 0.2s ease, background-color 0.2s ease;
            }
            .photo-sheet__btn:disabled { opacity: 0.55; cursor: not-allowed; }
            .photo-sheet__btn--ghost {
              background: transparent;
              border: 1px solid rgba(255, 255, 255, 0.18);
              color: #F4EFE7;
            }
            .photo-sheet__btn--gold {
              background: #D4AF37;
              border: none;
              color: #0A0A0F;
            }
          `}</style>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default ProfilePhotoSheet;
